import { EditOutlined } from '@ant-design/icons';
import { Button, DatePicker, Select, message } from 'antd';
import { omsOrderRecycleOrderUpdateQuote } from 'apis/oms';
import LOCALS from 'commons/locals';
import dayjs from 'dayjs';
import i18n from 'i18n';
import { getSelectableStaffOptions } from './modify-base-info.utils';
import { resolveReceptionistLabel } from './staff-utils';
import { GlobalInfoState } from 'store/slices/globalInfoSlice';
import { formatQuoteTimeWithZone } from 'utils/quote-time';
import { useMemo, useRef, useState } from 'react';

type StaffOptions = GlobalInfoState['staffSelectOptions'];

const saveQuote = async (
  orderId: string,
  payload: { quoteStaffId?: number | null; quoteTime?: string | null },
  onSuccess: () => void,
) => {
  await omsOrderRecycleOrderUpdateQuote({
    omsRecycleOrderId: orderId,
    ...payload,
  });
  message.success(i18n.t(LOCALS.successful_operation));
  onSuccess();
};

export const EditableQuoteStaff = ({
  orderId,
  quoteStaffId,
  staffSelectOptions,
  onSuccess,
}: {
  orderId: string;
  quoteStaffId?: number | null;
  staffSelectOptions: StaffOptions;
  onSuccess: () => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const changedRef = useRef(false);
  const options = useMemo(() => {
    const activeOptions = getSelectableStaffOptions(staffSelectOptions);
    if (quoteStaffId == null) return activeOptions;
    if (activeOptions.some((option) => option.value === quoteStaffId)) {
      return activeOptions;
    }
    return [
      ...activeOptions,
      {
        value: quoteStaffId,
        label:
          resolveReceptionistLabel(staffSelectOptions, quoteStaffId) ||
          `${quoteStaffId}`,
      },
    ];
  }, [quoteStaffId, staffSelectOptions]);
  const label =
    resolveReceptionistLabel(staffSelectOptions, quoteStaffId) || '-';

  const handleChange = async (value?: number | string | null) => {
    changedRef.current = true;
    const nextId = value == null || value === '' ? null : Number(value);
    if (nextId === (quoteStaffId ?? null)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await saveQuote(orderId, { quoteStaffId: nextId }, onSuccess);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {editing ? (
        <Select
          className="min-w-[160px]"
          autoFocus
          showSearch
          allowClear
          loading={saving}
          defaultOpen
          value={quoteStaffId ?? undefined}
          placeholder={i18n.t(LOCALS.please_select) || ''}
          options={options}
          filterOption={(input, option) =>
            String(option?.label || '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          onChange={(value) => {
            void handleChange(value);
          }}
          onDropdownVisibleChange={(open) => {
            if (open) return;
            window.setTimeout(() => {
              if (!changedRef.current && !saving) setEditing(false);
              changedRef.current = false;
            }, 0);
          }}
        />
      ) : (
        <div>{label}</div>
      )}
      <Button
        icon={<EditOutlined />}
        disabled={saving}
        onClick={() => setEditing(true)}
      />
    </div>
  );
};

export const EditableQuoteTime = ({
  orderId,
  quoteTime,
  onSuccess,
}: {
  orderId: string;
  quoteTime?: string | null;
  onSuccess: () => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const changedRef = useRef(false);

  const handleChange = async (value: dayjs.Dayjs | null) => {
    changedRef.current = true;
    const nextTime = value ? formatQuoteTimeWithZone(value) : null;
    const current = quoteTime ? formatQuoteTimeWithZone(quoteTime) : null;
    if (nextTime === current) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await saveQuote(orderId, { quoteTime: nextTime }, onSuccess);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {editing ? (
        <DatePicker
          autoFocus
          allowClear
          showTime={{ format: 'HH:mm' }}
          format="YYYY-MM-DD HH:mm"
          defaultOpen
          disabled={saving}
          value={quoteTime ? dayjs(quoteTime) : null}
          onChange={(value) => {
            void handleChange(value);
          }}
          onOpenChange={(open) => {
            if (open) return;
            window.setTimeout(() => {
              if (!changedRef.current && !saving) setEditing(false);
              changedRef.current = false;
            }, 0);
          }}
        />
      ) : (
        <div>
          {quoteTime ? dayjs(quoteTime).format('YYYY-MM-DD HH:mm') : '-'}
        </div>
      )}
      <Button
        icon={<EditOutlined />}
        disabled={saving}
        onClick={() => setEditing(true)}
      />
    </div>
  );
};
